import React from 'react';
import type { GuideViewVariant, ItemType } from '../../../../types/enums';
import type { ItemViewVariantFC } from '../../../entities/types';
import type { Guide } from '@aurora/shared-generated/types/graphql-schema-types';
import { useClassNameMapper } from 'react-bootstrap';
import GuideAvatar from '../../GuideAvatar/GuideAvatar';
import GuideArticleLink from '../GuideArticleLink';
import localStyle from './GuideViewCard.module.pcss';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import Panel from '@aurora/shared-client/components/common/Panel/Panel';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import { GuideCardSize } from '../../enums';
import Icons from '@aurora/shared-client/public/static/graphics/processed/enums';

/**
 * Helper function to return an icon size based on a card size
 * @param selectedCardSize The card size to retrieve the icon size against
 */
function getGuideIconSize(selectedCardSize: GuideCardSize): IconSize {
  switch (selectedCardSize) {
    case GuideCardSize.XS: {
      return IconSize.PX_50;
    }
    case GuideCardSize.SM: {
      return IconSize.PX_80;
    }
    default: {
      return IconSize.PX_160;
    }
  }
}

/**
 * Guide View - Card
 *
 * @constructor
 * @author Amit Agrawal
 */
const GuideViewCard: ItemViewVariantFC<Guide, ItemType.GUIDE, GuideViewVariant.CARD> = ({
  entity: guide,
  className,
  useDescription,
  useAvatar,
  cardSize,
  textAlignment,
  clampDescriptionLines
}) => {
  const cx = useClassNameMapper(localStyle);
  const iconSize = getGuideIconSize(cardSize);

  return (
    <Panel
      className={cx(
        className,
        'lia-g-card lia-guide-card',
        {
          'lia-g-card-sm': cardSize === GuideCardSize.SM
        },
        {
          'lia-g-card-xs': cardSize === GuideCardSize.XS
        }
      )}
    >
      <div
        className={cx(
          'lia-g-card-body lia-g-pb-0',
          { 'lia-g-card-left lia-card-grid-left': textAlignment === TextAlignment.LEFT },
          { 'lia-g-card-right lia-card-grid-right': textAlignment === TextAlignment.RIGHT },
          { 'lia-g-card-center': textAlignment === TextAlignment.CENTER }
        )}
      >
        {useAvatar && (
          <GuideAvatar
            className={cx(
              'lia-avatar lia-g-card-avatar',
              {
                'lia-avatar-sm': cardSize === GuideCardSize.SM
              },
              { 'lia-avatar-xs': cardSize === GuideCardSize.XS }
            )}
            guide={guide}
            size={iconSize}
            fallback={(): JSX.Element => (
              <Icon
                icon={Icons.GuideIcon}
                size={iconSize}
                color={IconColor.GRAY_700}
                className={cx(
                  'lia-g-card-avatar lia-avatar',
                  {
                    'lia-avatar-sm': cardSize === GuideCardSize.SM
                  },
                  { 'lia-avatar-xs': cardSize === GuideCardSize.XS }
                )}
                useFrame
              />
            )}
          />
        )}
        <GuideArticleLink
          guide={guide}
          useStretchLink={true}
          className={cx('lia-guide-title', {
            'lia-guide-title-base': cardSize === GuideCardSize.SM || cardSize === GuideCardSize.XS
          })}
        />
        {useDescription && guide.description && (
          <p
            className={cx(`lia-guide-description lia-g-clamp lia-g-clamp-${clampDescriptionLines}`)}
          >
            {guide.description}
          </p>
        )}
      </div>
    </Panel>
  );
};
export default GuideViewCard;
